'use client'

import { Grid2X2, Sparkles } from 'lucide-react'

import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from '@/shared/components/molecules/Dialog/dialog'

interface SlotTypeSelectionModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	onSelectRegular: () => void
	onSelectSmart: () => void
}

export const SlotTypeSelectionModal = ({
	open,
	onOpenChange,
	onSelectRegular,
	onSelectSmart,
}: SlotTypeSelectionModalProps) => {
	const { t } = useTranslation()
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-2xl p-0 gap-0">
				<DialogHeader className="px-6 pt-6 pb-4">
					<DialogTitle className="text-xl">
						{t('entities.schedule.slot_type.select_type_title')}
					</DialogTitle>
					<DialogDescription className="sr-only">
						{t('entities.schedule.slot_type.select_type_description')}
					</DialogDescription>
				</DialogHeader>

				<Separator />

				<div className="px-6 py-6 space-y-3">
					{/* Regular Slot Option */}
					<button
						onClick={onSelectRegular}
						className="w-full p-4 rounded-lg border border-muted hover:bg-accent/50 transition-colors text-left"
					>
						<div className="flex items-start gap-4">
							<div className="flex-shrink-0 w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
								<Grid2X2 className="w-6 h-6" />
							</div>
							<div className="flex-1">
								<h3 className="font-semibold text-base">
									{t('entities.schedule.slot_type.regular_slot')}
								</h3>
								<p className="text-sm text-muted-foreground mt-1">
									{t('entities.schedule.slot_type.regular_slot_description')}
								</p>
							</div>
						</div>
					</button>

					{/* Smart Slot Option */}
					<button
						onClick={onSelectSmart}
						className="w-full p-4 rounded-lg border border-muted hover:bg-accent/50 transition-colors text-left"
					>
						<div className="flex items-start gap-4">
							<div className="flex-shrink-0 w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
								<Sparkles className="w-6 h-6" />
							</div>
							<div className="flex-1">
								<h3 className="font-semibold text-base">
									{t('entities.schedule.slot_type.smart_slot')}
								</h3>
								<p className="text-sm text-muted-foreground mt-1">
									{t('entities.schedule.slot_type.smart_slot_description')}
								</p>
							</div>
						</div>
					</button>
				</div>

				<Separator />

				<DialogFooter className="px-6 py-4">
					<Button
						type="button"
						variant="outline"
						onClick={() => onOpenChange(false)}
						className="h-8 rounded-full px-6"
					>
						{t('actions.cancel')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
